import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Table from "../../../core/common/dataTable/index";
import { getAuditLogs } from "../../../core/data/redux/auditLogsSlice";
import { all_routes } from "../../router/all_routes";
import { formatDateTime } from "../../../utils/dateUtils";

const AuditLogs = () => {
  const routes = all_routes;
  const dispatch = useDispatch();
  const auditLogState = useSelector((state: any) => state.auditLogState);

  const [dataList, setDataList] = useState<any[]>([]);
  const [query, setQuery] = useState<string>("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 10,
  });

  const fetchRecords = (pagination: any) => {
    dispatch(
      getAuditLogs({
        page: pagination.current,
        limit: pagination.pageSize,
      })
    );
  };

  const handleRefresh = () => {
    fetchRecords(pagination);
  };

  const handlePagination = (paginationEvent: any) => {
    const newPagination = {
      current: paginationEvent.current,
      pageSize: paginationEvent.pageSize,
      total: pagination.total, // Keep existing total
    };
    setPagination(newPagination);
    fetchRecords(newPagination);
  };



  useEffect(() => {
    fetchRecords(pagination);
  }, []);

  useEffect(() => {
    if (
      auditLogState.loading === "fulfilled" &&
      auditLogState.action === "list"
    ) {
      setDataList(auditLogState.data?.data?.data || []);
      setPagination((prev) => ({
        ...prev,
        total: auditLogState.data?.data?.total || 0,
        pageSize: auditLogState.data?.data?.limit || prev.pageSize,
      }));
    } else if (auditLogState.loading === "rejected") {
      toast.error(auditLogState?.error || "Failed to fetch OTP logs");
    }
  }, [auditLogState.loading, auditLogState.action]);

  const columns = [
    {
      title: "Sr. No.",
      dataIndex: "id",
      render: (text: string, record: any, index: number) => (
        <>
          <div>
            {pagination.current
              ? index +
              pagination.current * pagination.pageSize -
              pagination.pageSize +
              1
              : index + 1}
          </div>
        </>
      ),
      sorter: (a: any, b: any) => a.id.length - b.id.length,
    },

    {
      title: "Table",
      dataIndex: "entity",
      render: (text: string) => <span>{text}</span>,
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (text: string) => <span>{text}</span>,
    },
    {
      title: "Payload",
      dataIndex: "payload",
      width: 400,
      render: (text: any) => (
        <pre
          style={{
            backgroundColor: "#d4edda",
            padding: "8px 12px",
            borderRadius: "8px",
            maxHeight: "200px",
            minHeight: "200px",
            overflowY: "auto",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontSize: "12px",
            color: "#155724"
          }}
        >
          {typeof text === "object"
            ? JSON.stringify(text, null, 2)
            : (() => {
              try {
                return JSON.stringify(JSON.parse(text), null, 2);
              } catch {
                return text || "";
              }
            })()}
        </pre>
      ),
    },
    {
      title: "Data Updated",
      dataIndex: "data",
      width: 400,
      render: (text: any) => (
        <pre
          style={{
            backgroundColor: "#d4edda",
            padding: "8px 12px",
            borderRadius: "8px",
            maxHeight: "200px",
            minHeight: "200px",
            overflowY: "auto",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontSize: "12px",
            color: "#155724"
          }}
        >
          {typeof text === "object"
            ? JSON.stringify(text, null, 2)
            : (() => {
              try {
                return JSON.stringify(JSON.parse(text), null, 2);
              } catch {
                return text || "";
              }
            })()}
        </pre>
      ),
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      render: (text: string) =>
        text ? formatDateTime(text) : "N/A",
    },
    {
      title: "Action By (ID)",
      dataIndex: "action_by",
      render: (action_by: string) => <span>{action_by}</span>,
    },
    {
      title: "Action By (Type)",
      dataIndex: "action_by_type",
      render: (text: string) => <span>{text}</span>,
    },
  ];

  return (
    <>
      <style>
        {`
          .spin {
            animation: spin 1s linear infinite;
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Audit Logs</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="#">Logs</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Audit Logs
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="mb-2">
                <button
                  className="btn btn-primary d-flex align-items-center"
                  onClick={handleRefresh}
                  disabled={auditLogState.loading === "pending"}
                  title="Refresh OTP Logs"
                >
                  <i
                    className={`ti ti-refresh me-2 ${auditLogState.loading === "pending" ? "spin" : ""
                      }`}
                  />
                  {auditLogState.loading === "pending"
                    ? "Refreshing..."
                    : "Refresh"}
                </button>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-4 mt-3"> Audit Logs</h4>
            </div>
            <div className="card-body p-0 py-3">
              <Table
                columns={columns}
                dataSource={dataList}
                Selection={false}
                paginationConfig={pagination}
                handlePagination={handlePagination}
                loading={
                  auditLogState.loading === "pending" &&
                  auditLogState.action === "list"
                }
              //handleSearch={handleSearch}
              >
                <div></div>
              </Table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AuditLogs;
